import { DataBlockDictionaryItemDto, QueryFactory } from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useDictionaryByTitle,
  useDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';

import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import globalStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import {
  FieldStatus,
  getPatientFields,
} from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { MergePatientsFieldsTable } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/MergePatientsFieldsTable';
import { MergePatientsResult } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/MergePatientsResult';
import localStyles from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/PatientDataHeader.module.scss';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CardData } from '@lmt/unicorn-shared/app/components/card-reader/components/CardReaderHandler';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './components/MergePair.module.scss';
import { ToastIcon } from '../../common/icons/toast/ToastIcon';

type Props = {
  healthInsuranceCompany: DataBlockDictionaryItemDto;
  cardData: CardData;
};

const insuranceCompanyNameField = 'healthInsuranceCompany';
const shortNameField = 'Short name';

export const MergeInsuranceCardData: FC<Props & FormComponentProps> = (
  props,
) => {
  const queryClient = useQueryClient();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18nMerge = useScopedTranslation(
    'merge_assistant.merge_with_card_data',
  );

  const {
    healthInsuranceCompany,
    cardData,
    formType,
    formId,
    tryCompleteForm,
  } = props;

  const insuranceCompanyDictionary = useDictionaryByTitle(
    CustomDictionaryName.InsuranceCompanies,
  );
  const ignoredInsuranceCompanyNamesDictionary = useDictionaryByTitle(
    CustomDictionaryName.IgnoredInsuranceCompanyNames,
  );
  const ignoredInsuranceCompanyNames = useDictionaryValuesByTitle(
    CustomDictionaryName.IgnoredInsuranceCompanyNames,
    cardData.healthInsuranceCompany,
  );

  const [insuranceFields, setInsuranceFields] = useState<{
    [key: string]: FieldStatus;
  }>({});

  const healthInsuranceCompanyDict: { [key: string]: any } = useMemo(() => {
    return {
      healthInsuranceCompany: healthInsuranceCompany.values[
        shortNameField
      ] as string,
    };
  }, [healthInsuranceCompany]);

  const cardDataDict: { [key: string]: any } = useMemo(() => {
    return {
      healthInsuranceCompany: cardData.healthInsuranceCompanyShortName,
    };
  }, [cardData]);

  useEffect(() => {
    setInsuranceFields(
      getPatientFields(healthInsuranceCompanyDict, cardDataDict),
    );
  }, [healthInsuranceCompanyDict, cardDataDict]);

  const changeFieldStatus = useCallback((key: string, value: FieldStatus) => {
    setInsuranceFields((prevValues) => {
      return {
        ...prevValues,
        [key]: value,
      };
    });
  }, []);

  const getInternalOrCardValue = useCallback(
    (fieldName: string) => {
      const fieldStatus = insuranceFields[fieldName];
      if (fieldStatus && fieldStatus !== FieldStatus.const) {
        return fieldStatus === FieldStatus.firstPatient
          ? healthInsuranceCompanyDict[fieldName]
          : fieldStatus === FieldStatus.secondPatient
            ? cardDataDict[fieldName]
            : undefined;
      }
      return undefined;
    },
    [insuranceFields],
  );

  const updateInsuranceCompany = useCallback(async () => {
    try {
      const shortName = getInternalOrCardValue(
        insuranceCompanyNameField,
      )?.trim();
      if (!shortName) {
        return;
      }
      let values = { ...healthInsuranceCompany.values };
      values[shortNameField] = shortName;
      const fullName = `${shortName}, ${cardData.healthInsuranceCompanyNumber}`;

      await tryCompleteForm(
        formType,
        formId,
        async () => {
          await QueryFactory.DataBlockDictionaryItemClient.patch(
            healthInsuranceCompany.id,
            {
              value: fullName,
              values,
            },
          );
          if (ignoredCompanyNameItem?.value === fullName) {
            // remove ignored name from dictionary
            await QueryFactory.DataBlockDictionaryItemClient.remove(
              ignoredCompanyNameItem!.id,
            );
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                ignoredInsuranceCompanyNamesDictionary?.id,
              ),
            });
          }
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
              insuranceCompanyDictionary?.id,
            ),
          });
          toast(i18nMerge.t('insurance_company_merge_successful_toast'), {
            icon: () => <ToastIcon type="success" />,
          });
        },
        i18nMerge.t('saving'),
      );
    } catch (err) {
      await modals.showError({ text: parseError(err) });
    }
  }, [
    healthInsuranceCompany,
    getInternalOrCardValue,
    formType,
    formId,
    tryCompleteForm,
    dispatch,
  ]);

  const ignoreInsuranceCompanyCardName = useCallback(
    async (companyCardName: string, companyId: number) => {
      const isConfirmed = await modals.showConfirm({
        title: i18nMerge.t('confirm_ignore_card_data_title'),
        text: i18nMerge.t('confirm_ignore_card_data_text'),
        okButtonText: i18nMerge.t('confirm_ignore_card_data_button'),
      });
      if (isConfirmed) {
        await tryCompleteForm(
          formType,
          formId,
          async () => {
            if (!ignoredInsuranceCompanyNamesDictionary) {
              return;
            }
            await QueryFactory.DataBlockDictionaryItemClient.create({
              dictionaryId: ignoredInsuranceCompanyNamesDictionary.id,
              value: companyCardName,
              values: {
                'Insurance company': companyId,
              },
            });
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                ignoredInsuranceCompanyNamesDictionary.id,
              ),
            });
          },
          i18nMerge.t('saving'),
        );
      }
    },
    [ignoredInsuranceCompanyNamesDictionary],
  );

  const ignoredCompanyNameItem = useMemo(() => {
    return ignoredInsuranceCompanyNames.data?.data.find(
      (x) => x.value === cardData.healthInsuranceCompany && !x.isDeleted,
    );
  }, [cardData, ignoredInsuranceCompanyNames]);

  return (
    <div
      className={clsx(
        globalStyles.autoSizedContainer,
        globalStyles.autoSizedContainerWithScroll,
      )}
    >
      <div className={styles.dataContainer}>
        <div className={styles.rootGrid}>
          <div className={styles.valuesContainer}>
            <div className={styles.row}>
              <div className={styles.fieldTitle}>
                {i18nMerge.t('data_source')}
              </div>
              <div className={localStyles.fieldWithBorder}>
                <div className={localStyles.boldTitle}>
                  {i18nMerge.t('unicorn_source')}
                </div>
              </div>
              <div />
              <div className={localStyles.fieldWithBorder}>
                <div className={localStyles.boldTitle}>
                  {i18nMerge.t('insurance_card_source')}
                </div>
              </div>
            </div>
          </div>
          <MergePatientsFieldsTable
            fields={insuranceFields}
            changeField={changeFieldStatus}
            customFields={{}}
            changeCustomField={undefined}
            getFirstPatientValue={(fieldName) =>
              healthInsuranceCompanyDict[fieldName]
            }
            getSecondPatientValue={(fieldName) => cardDataDict[fieldName]}
            getFirstPatientCustomValue={undefined}
            getSecondPatientCustomValue={undefined}
          />
        </div>
        <MergePatientsResult
          fields={insuranceFields}
          customFields={{}}
          getFirstPatientValue={(fieldName) =>
            healthInsuranceCompanyDict[fieldName]
          }
          getSecondPatientValue={(fieldName) => cardDataDict[fieldName]}
          getFirstPatientCustomValue={undefined}
          getSecondPatientCustomValue={undefined}
        />
      </div>
      <div
        className={clsx(globalStyles.submitContainer, styles.controlsContainer)}
      >
        <div>
          {!ignoredCompanyNameItem && (
            <Button
              className={globalStyles.submit}
              color={ButtonColor.Danger}
              title={i18nMerge.t('ignore_card_data_button')}
              onClick={() =>
                ignoreInsuranceCompanyCardName(
                  cardData.healthInsuranceCompany,
                  healthInsuranceCompany.id,
                )
              }
            />
          )}
        </div>
        <div className={styles.controls}>
          <Button
            disabled={Object.values(insuranceFields).includes(
              FieldStatus.notSelected,
            )}
            className={globalStyles.submit}
            title={i18nMerge.t('confirm_button')}
            onClick={updateInsuranceCompany}
          />
        </div>
      </div>
    </div>
  );
};
